import { ValueOf } from '@ahha/utils/@types/typeUtils'
import { ComboKeys, NormalKeys } from '@ahha/utils/hooks/Accessibility/useHotKeys'

import { NodeItem, BaseNodeInfo, CommonListPayload } from '@/api/Node/types'
import { UserInfo } from '@/api/User/types'
import { Paginated } from '@/api/Base/types'
import { WeightScore } from '@/api/ModelNode/types'
import { Edge, PROJECT_NODE_TYPE, ProjectNodeType } from '@/api/Project/types'
import { LabelingClass as LabelingClassType } from '@/api/ClassGroup/types'
import { TabularLabelingItem, TabularValueType } from '@/api/Tabular/types'
import { HyperparametersBase } from '@/api/Hyperparameters/types'
import { ModelRetrainNodeInfo } from '@/api/ModelRetrainNode/types'
import { LABELING_STATUS, LABELING_TOOL_TYPE } from '@/api/LabelingNode/const'

import { ModelValueType, TabularRequestStatusType, TaskStatusValueType } from '@/pages/const'
import { OS_TYPE } from '@/pages/Labeling/components/ShortcutDrawer/hotKeys'

export type LabelingNodeBase = {
  confirmedAt: string | null,
  labelingFinishedAt: string | null,
  modelType: ModelValueType | null,
  nodeType: 'LABELING'
  // datasetNodeIds: string[],
  // preprocessingResultNodeIds: string[],
  isAuto: boolean
  pipelineType: 'BASIC' | 'OPERATIONS'
  datasetCategory: 'IMAGE' | 'TABULAR' | 'VIDEO'
  isEditBlocked: boolean
  labelerId?: string
  isLabelerAssigned: boolean,
  labelingMembers: {
    userId: string,
    name: string,
    profileImageAccessibleUrl?: string,
    profileColor?: string
    hasObjects: boolean
  }[] | null,
  reviewers: { userId: string, name: string, profileColor?: string, profileImageAccessibleUrl?: string, hasObjects: boolean, }[],
  numOfDatasetItems: number,
  numOfLabelingItems: number,
  numOfCompletedItems: number,
  numOfFinishedItems: number,
  numOfNotFinishedItems: number,
  numOfNotReviewedItems: number,
  numOfRejectedItems: number,
  numOfNormalItems?: number, // 정상 이미지 개수
  status: LabelingStatus,
  classGroupId: string,
  classGroupName: string,
  isTempClassGroup?: boolean,
  hasClusteringResult: boolean,
  hasSelectedForClustering?: boolean,
  jobStatus?: TaskStatusValueType
  labelingJobId: string | null
  canRunSemiAutoLabeling: boolean // ops에서만 사용
  tabularRequestStatus?: TabularRequestStatusType
  edaMetadataId?: string
  labeledRows?: number
  hasMissingCount?: boolean
  tabularType?: TabularValueType
  readOnly?: boolean
  retrainSemiAutoSettings: RetrainSemiAutoSettingsType
  retrainExternalModelSemiAutoSettings: {
    confidenceThreshold: number
    labelingClassIds: string[]
    externalModelId: string
    weightScore?: WeightScore | null
  }
  isUsedAsDriftReference?: boolean
  dependentColumns?: string[]
  independentColumns?: string[]
}

export type LabelingNodeInfo = BaseNodeInfo<LabelingNodeBase>

export type LabelingStatus = ValueOf<typeof LABELING_STATUS>

export type LabelingToolType = ValueOf<typeof LABELING_TOOL_TYPE>

export type LabelingClass = {
  createdBy: string,
  updatedBy: string | null,
  createdAt: Date,
  updatedAt: Date | null,
  _id: string,
  labelingNodeId?: string, // main node
  name: string,
  color: string,
  metadata: any,
  order: number | null,
}

export type Comment = {
  commentId: string,
  commentedBy: string,
  comment: string,
  commentedAt: Date,
  name: string,
  profileImageAccessibleUrl?: string | null,
  profileColor?: string,
}

export type LabelingObject = {
  createdBy: string,
  updatedBy: string | null,
  createdAt: Date,
  updatedAt: Date | null,
  _id: string,
  labelingItemId: string,
  zOrder: number,
  isPathEditable: boolean,
  isFixedRatio: boolean,
  region: any,
  metadata: any,
  text: string,
  labelingClass: LabelingClass,
  type: ObjectToolValueType,
  isConfirm: boolean | null,
  reviewedBy: unknown,
  comments: Comment[] | null,
  toolType: LabelingToolType,
  // 학습 제외 영역(마스킹) 오브젝트 구분용. 확장성 위해 string. 없으면 일반 오브젝트
  roiToolType?: string | null,
  confidenceScore?: number | null,
  startMs?: number | null,
  endMs?: number | null,
  videoFileId?: string | null,
  startFrameNumber?: number | null,
  endFrameNumber?: number | null,
  memoContent?: string | null,
}

type LabelingNodeItemBase = {
  nodeType: typeof PROJECT_NODE_TYPE.LABELING,
  labelingNodeId: string,
  datasetItemId: string,
  labelerId: string,
  status: LabelingStatus,
  labeler: UserInfo,
  numOfLabelObjects: number,
  labelingObjects: LabelingObject[],
  isSelectedForClustering: boolean,
  isRunningLabelAssistant: boolean,
}
export type LabelingNodeItem = NodeItem<LabelingNodeItemBase>

export type UserShortcut = {
  action: string,
  shortcutKey: NormalKeys | ComboKeys,
  type: ValueOf<typeof OS_TYPE>,
}

/** Response */
export type LabelingCommonResponse = {
  createdBy: string,
  updatedBy: string,
  createdAt: Date,
  updatedAt: Date,
  _id: string,
  userId: string,
}

export type GetLabelingNodeItemsPayload = CommonListPayload & {
  status?: string
  isSelectedForClustering?: boolean
}

export type GetLabelAssistantItemsPayload = CommonListPayload & {
  isCreatedByUser?: boolean
  classes?: string
}

export type UpdateLabelAssistantPayload = {
  labelingNodeId: string
  labelingItemId: string
  labelingPromptItemIds: string[]
}

export type UpdateLabelAssistantResponse = {
  labelingItem: LabelingNodeItem
  hasAutoLabelingObjects: boolean
}

export type GetLabelingNodeItemsResponse = Paginated<LabelingNodeItem[]>

export type GetUserMetaResponse = UpdateUserMetaPayload & LabelingCommonResponse

export type GetUserShortcutsResponse = {
  shortCutKeySettings: UserShortcut[],
} & LabelingCommonResponse

export type CreateLabelingNodePayload = {
  projectId: string
  name?: string
  metadata?: any
  sourceNodeType?: ProjectNodeType
  edge?: Edge
  position: { x: number, y: number }
}

/** Payload */
export type UpdateLabelingNodeSettingsPayload = {
  labelingNodeId: string
  modelType: ModelValueType
  deleteAllObjects: boolean
  labelers: { userId: string, shouldDelete: boolean, deleteObjects: boolean }[]
  reviewers: { userId: string, shouldDelete: boolean, deleteObjects: boolean }[]
}

export type UpdateAssignedLabelersPayload = {
  labelingNodeId: string
  updatedItems: { labelerId: string, labelingItemId: string }[]
}

export type UpdateLabelingNodePayload = {
  labelingNodeId: string
  datasetNodeIds?: string[]
  metadata?: any
  name?: string
}

export type AddClassGroupReferencePayload = {
  labelingNodeId: string
  classGroupId: string
}

export const OBJECT_TOOL_TYPE = {
  RECTANGLE: 'RECTANGLE',
  ELLIPSE: 'ELLIPSE',
  POLYGON: 'POLYGON',
  TAG: 'TAG',
  COMMENT: 'COMMENT',
  VIDEO_SEGMENT: 'VIDEO_SEGMENT',
} as const

export type ObjectToolValueType = ValueOf<typeof OBJECT_TOOL_TYPE>

export const ROI_TOOL_TYPE = {
  MASKING: 'MASKING',
} as const

export type RoiToolValueType = ValueOf<typeof ROI_TOOL_TYPE>

export type SaveObjectsPayload = {
  labelingItemId: string,
  labelingObjects: {
    isFixedRatio?: boolean,
    isPathEditable?: boolean,
    // 학습 제외 영역(마스킹)은 클래스 없이 저장되므로 optional
    labelingClassId?: string,
    labelingObjectId?: string,
    metadata?: any,
    region?: any,
    text?: string,
    type: ObjectToolValueType,
    zOrder: number,
    toolType?: LabelingToolType,
    roiToolType?: string,
  }[],
}

export type CreateObjectPayload = {
  labelingItemId: string,
  // 학습 제외 영역(마스킹)은 클래스 없이 저장되므로 optional
  labelingClassId?: string,
  type: ObjectToolValueType,
  zOrder: number,
  isPathEditable?: boolean,
  isFixedRatio?: boolean,
  region?: any,
  metadata?: any,
  text?: string,
  // isCreatedByUser?: boolean,
  toolType?: LabelingToolType,
  // 학습 제외 영역(마스킹) 오브젝트 구분용. 없으면 일반 오브젝트
  roiToolType?: string,
}

export type CreateVideoObjectPayload = {
  labelingItemId: string
  videoFileMetaId: string
  type: typeof OBJECT_TOOL_TYPE.VIDEO_SEGMENT
  startMs: number
  endMs: number
  labelingClassId?: string
  metadata?: any
}

export type UpdateVideoObjectPayload = {
  labelingItemId: string
  labelingObjectId: string
  videoFileMetaId: string
  type: typeof OBJECT_TOOL_TYPE.VIDEO_SEGMENT
  startMs: number
  endMs: number
  labelingClassId: string
  metadata?: any
}

export type UpdateVideoObjectClassPayload = {
  labelingNodeId: string
  labelingClassId: string
}

export type UpdateObjectPayload = Omit<CreateObjectPayload, 'labelingItemId'> & {
  labelingObjectId: string
}

export type DeleteObjectsPayload = {
  labelingItemId: string,
  labelingObjectIds: string[]
}

export type GetMagicWandObjectPayload = {
  labelingItemId: string
  x?: number,
  y?: number,
  box?: [number, number, number, number] // [xtl, ytl, xbr, ybr]
  type: 'POINT' | 'BOX'
}

export type GetMagicWandObjectResponse = {
  result: {
    region: any
    type: 'POLYGON' | 'RECTANGLE'
  }[],
  input: {
    image: string
    point: [number, number]
  }
}

export type UpdateUserMetaPayload = {
  labelingNodeId: string,
  duration: number,
  lastIndex: number,
  lastLabelingItemId: string,
}

export type UpdateUserShortcutsPayload = {
  shortcutKeySettings: UserShortcut[]
}

export interface CreateCommentPayload {
  comment: string
  labelingItemId: string
  metadata?: any
  region: Record<string, any>
}

export interface AddCommentPayload {
  labelingObjectId: string
  comment: string
}

export type GetCommentListPayload = CommonListPayload & {
  labelingNodeId: string
  labelingItemId?: string
}

export type GetCommentListResponse = Paginated<LabelingObject[]>

export interface UndoRedoResponse {
  currentObjects: LabelingObject[] | null
  labelingHistory: Omit<ItemHistory, 'user | labelingObjects'>
}

/* CLASSIFICATION */
export type GetClassificationItemsPayload = Omit<CommonListPayload, 'keyword'> & {
  labelingNodeId: string
  status?: string
  classes?: string
  tag?: string
  isSelectedForClustering?: boolean
}

export type GetClassificationItemsResponse = Paginated<LabelingNodeItem[]>

export type CreateTagObjectPayload = {
  labelingItemId: string,
  labelingClassId: string,
  zOrder?: number,
  metadata?: any,
}

export type CreateTagToImagesPayload = {
  labelingNodeId: string,
  taggedItems: { labelingClassId: string, labelingItemId: string }[],
  finishAllTaggedItems: boolean;
}

/* Review Node */
export type ReviewNodeBase = LabelingNodeBase & {
  labelingNodeId?: string
}

export type ReviewNodeInfo = BaseNodeInfo<ReviewNodeBase>

export type GetAllDatasetsForReviewResponse = {
  reviewDatasets: ReviewNodeInfo[]
  labelingDatasets: LabelingNodeInfo[]
}

export type UpdateObjectOrder = {
  labelingObjectId: string,
  zOrder: number,
}

export type ItemHistory = {
  action: 'CREATE' | 'UPDATE' | 'DELETE'
  backupObjects: LabelingObject[]
  createdAt: string
  createdBy: string | null
  isDone: boolean
  labelingItemId: string
  labelingObjects: LabelingObject[]
  undo: boolean
  updatedAt: string | null
  updatedBy: string | null
  user: UserInfo
  userId: string
  _id: string
}

export type ClusteringInfo = {
  total: number
  list: {
    accessibleUrl: string
    label: number
    labelingItem: LabelingNodeItem
    labelingItemId: string
    labelingNodeId: string
    tsneXY: [number, number]
    _id: string
  }[]
}

export type RunClusteringPayload = {
  labelingNodeId: string
  crowdCount: number
}

export type SelectClusteringItemsPayload = {
  labelingNodeId: string
  labelingItemIds: string[]
}

export type RunSemiAutoLabelingPayload = {
  labelingNodeId: string
  labelingClassIds: string[]
  modelNodeId?: string
  externalModelId?: string
  confidenceThreshold?: number
  weightScore?: WeightScore
}

export type SemiAutoLabelingModelItem = {
  _id: string
  isExternalModel: boolean
  modelType: ModelValueType
  name: string
  createdBy: string
  creatorName: string
  creatorProfileColor: string
  creatorProfileImageAccessibleUrl: string
  epochs?: number | null
  hasRoi?: boolean | null
  labelingClassIds?: string | null
  labelingClasses?: LabelingClass[] | null
  numOfLabelingItems?: number | null
  preset?: HyperparametersBase | null
  threshold?: number | null
  weightScores?: WeightScore[] | null
}

export type GetSemiAutoLabelingModelsResponse = {
  availableModels: SemiAutoLabelingModelItem[]
  estimatedDuration: number
}

export type UpdateSemiAutoLabelingThresholdPayload = {
  labelingNodeId: string
  reportId: string
  confidenceThreshold?: number
}

export interface DeleteSemiAutoLabelingObjectsCurrentFilter {
  boundaryRange?: number
  hasObjects?: boolean
  operation?: 'GTE' | 'LTE' | 'RANGE'
  rangeScore?: number
  score?: number
}

export interface DeleteSemiAutoLabelingObjectsPayload {
  labelingNodeId: string
  reportId: string
  currentFilter?: DeleteSemiAutoLabelingObjectsCurrentFilter
  labelingItemId?: string
  operation?: 'GT' | 'LT' | 'EQ' | 'GTE' | 'LTE'
  score?: number
}

export type SemiAutoLabelingReport = {
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string | null;
  _id: string;
  labelingNodeId: string;
  labelingItemId: string;
  reportId: string;
  lowestConfidenceScore: number;
  imageScore: number;
  displayScore: number;
  name: string;
  numOfObjects: number;
  numOfObjectsBelowConfidenceThreshold: number;
  needsReview: boolean;
  detectedTypes: DetectionType[];
  isSelectedForTraining: boolean;
  labelingObjects: LabelingObject[] | null;
  hasObjects: boolean;
}
export type GetSemiAutoLabelingItemsResponse = Paginated<SemiAutoLabelingReport[]>

export type SemiAutoLabelingItemsPayload = CommonListPayload & {
  hasObjects?: boolean
  operation?: 'GTE' | 'LTE' | 'RANGE'
  score?: number
  rangeScore?: number
  boundaryRange?: number
}

export const DETECTION_TYPE = {
  FALSE_NEGATIVE: 'FALSE_NEGATIVE',
  FALSE_POSITIVE: 'FALSE_POSITIVE',
} as const

export type DetectionType = ValueOf<typeof DETECTION_TYPE>

export type SemiAutoRetrainItem = {
  _id: string
  labelingNodeId: string
  labelingItemId: string
  reportId: string
  lowestConfidenceScore: number
  imageScore: number
  name: string
  numOfObjects: number
  needsReview: boolean
  detectedTypes: DetectionType
  numOfObjectsBelowConfidenceThreshold: number
  isSelectedForTraining: boolean
  labelingObjects: LabelingObject[]
  displayScore: number
  hasObjects: boolean
}

export type GetSemiAutoRetrainItemsResponse = Paginated<SemiAutoRetrainItem[]>

export type GetSemiAutoLabelingReportResponse = {
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  _id: string;
  labelingNodeId: string;
  labelingJobId: string;
  anoLabelingClass: string | null;
  modelNodeId: string;
  modelNodeName: string;
  confidenceThreshold: number;
  weightScores: WeightScore[];
  selectedWeightScore: WeightScore;
  totalObjectCount: number;
  totalItemCount: number;
  averageConfidenceScore: number;
  numOfObjectsBelowConfidenceThreshold: number;
  numOfItemsBelowConfidenceThreshold: number;
  numOfItemsSelectedForTraining?: number
  jobStatus?: TaskStatusValueType
  boundaryRange?: number
}

export type GetSemiAutoLabelingTaskResponse= {
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string | null;
  _id: string;
  name: string;
  nodeName: string;
  description: string;
  projectId: string;
  modelNodeId: string | null;
  validationNodeId: string | null;
  inferenceNodeId: string | null;
  preprocessingResultNodeId: string | null;
  dataDriftNodeId: string | null;
  labelingNodeId: string;
  hyperParameters: any | null;
  annotationExportObject: any;
  runningAINodeId: string | null;
  type: 'SEMI_AUTO_LABELING'
  status: TaskStatusValueType
  progress: number;
  threshold: number | null;
  maxCheckpointCount: number;
  preprocessJobs: any | null;
  roiCheck: boolean;
  input_size: any | null;
  weightFile: any | null;
  validationFile: any | null;
  preprocessFile: any | null;
  exportFile: any | null;
  result: any | null;
  meta: any;
  trainingModel: any | null;
  iouTh: any | null;
  jobPrepareStartedAt: string | null;
  jobPreparedAt: string | null;
  jobStartedAt: string | null;
  jobEndedAt: string | null;
  jobPausedAt: string | null;
  jobResumedAt: string | null;
  jobCanceledAt: string | null;
  jobTerminatedAt: string | null;
  jobErrorAt: string | null;
  initialCalculatedJobDurationInMs: number | null;
  estimatedJobEndAt: string | null;
  estimatedJobStartAt: string | null;
  weightScores: WeightScore[];
  weightScore: WeightScore;
  modelType: ModelValueType | null;
  countLossItem: number;
  isInsufficientCredit: boolean;
}

export type UpdateRetrainSemiAutoSettingsPayload = {
  labelingNodeId: string
  type: 'RETRAIN' | 'LARGE_MODEL' | 'EXTERNAL_MODEL'
  labelingClassIds: string[]
  confidenceThreshold?: number
  weightScore?: WeightScore
  externalModelId?: string
  modelVersionId?: string
  useRoi?: boolean
  manualReview: boolean
  minThreshold?: number
  maxThreshold?: number
}

export type RetrainSemiAutoSettingsType = {
  confidenceThreshold: number
  labelingClassIds: string[]
  modelVersionId: string
  modelVersionName: string
  weightScore?: WeightScore | null
}

export type ToggleUseRetrainPayload = {
  labelingNodeId: string
  reportItemId: string
}

export type GetTabularLabelingItemsResponse = Paginated<TabularLabelingItem[]>

export type GetRetrainSemiAutoSettingsResponse = {
  labelingClasses: LabelingClassType[]
  labelingNode: LabelingNodeInfo
  modelRetrainNode: ModelRetrainNodeInfo
  largeModelSettings?: {
    labelingClassIds: string[]
    modelVersionId: string
    modelVersionName: string
    useRoi: boolean | null
    weightScore: WeightScore | null
    proLabelThreshold?: number
    minThreshold?: number
    maxThreshold?: number
  }
  externalModelSettings?: {
    confidenceThreshold: number
    labelingClassIds: string[]
    externalModelId: string
    weightScore: WeightScore | null
  }
}

export const VariableRole = {
  DEPENDENT: 'DEPENDENT',
  INDEPENDENT: 'INDEPENDENT',
} as const

export type VariableRoleType = ValueOf<typeof VariableRole>

export type ToggleColumSelectionPayload = {
  labelingNodeId: string
  variableRole: VariableRoleType
  column: string
}
